import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'

interface RiesgosData {
	riesgos: {
		incendioExplosion?: boolean
		inundacion?: boolean
		derrumbesTempestad?: boolean
		usoExplosivos?: boolean
		erupcionVolcanica?: boolean
		antecedentesSismicos?: boolean
	}
	disenoAntisismico: string
	caracteristicasSubsuelo: string
	nivelFreatico: string
	cuerposAguaCercanos: string
	condicionesMeteorologicas: string
	edificacionesLinderas: string
	propiedadExistente: string
	coberturasAdicionales: string
	responsabilidadCivil: string
	equiposMaquinarias: string
	valoresRiesgo: string
	moneda: string
}

interface TodoRiesgoConstruccionRiesgosScreenProps {
	onNext: (data: RiesgosData) => void
	onBack: () => void
	data?: Partial<RiesgosData>
}

export function TodoRiesgoConstruccionRiesgosScreen({
	onNext,
	onBack,
	data,
}: TodoRiesgoConstruccionRiesgosScreenProps) {
	const [formData, setFormData] = useState<RiesgosData>({
		riesgos: data?.riesgos || {
			incendioExplosion: false,
			inundacion: false,
			derrumbesTempestad: false,
			usoExplosivos: false,
			erupcionVolcanica: false,
			antecedentesSismicos: false,
		},
		disenoAntisismico: data?.disenoAntisismico || '',
		caracteristicasSubsuelo: data?.caracteristicasSubsuelo || '',
		nivelFreatico: data?.nivelFreatico || '',
		cuerposAguaCercanos: data?.cuerposAguaCercanos || '',
		condicionesMeteorologicas: data?.condicionesMeteorologicas || '',
		edificacionesLinderas: data?.edificacionesLinderas || '',
		propiedadExistente: data?.propiedadExistente || '',
		coberturasAdicionales: data?.coberturasAdicionales || '',
		responsabilidadCivil: data?.responsabilidadCivil || '',
		equiposMaquinarias: data?.equiposMaquinarias || '',
		valoresRiesgo: data?.valoresRiesgo || '',
		moneda: data?.moneda || 'Pesos',
	})

	const toggleRiesgo = (key: keyof RiesgosData['riesgos']) => {
		setFormData({
			...formData,
			riesgos: {
				...formData.riesgos,
				[key]: !formData.riesgos[key],
			},
		})
	}

	const handleSubmit = () => {
		onNext(formData)
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-4">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Riesgos y Coberturas
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Información sobre riesgos del sitio y coberturas requeridas
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="rounded-xl bg-orange-50 p-3">
							<p className="font-inter text-xs font-medium text-orange-800">
								Riesgos del Sitio (marque los que apliquen)
							</p>
						</div>

						<div className="grid grid-cols-2 gap-2">
							{[
								{ key: 'incendioExplosion', label: 'Incendio / Explosión' },
								{ key: 'inundacion', label: 'Inundación' },
								{ key: 'derrumbesTempestad', label: 'Derrumbes / Tempestad' },
								{ key: 'usoExplosivos', label: 'Uso de explosivos' },
								{ key: 'erupcionVolcanica', label: 'Erupción volcánica' },
								{ key: 'antecedentesSismicos', label: 'Antecedentes sísmicos' },
							].map(({ key, label }) => (
								<label
									key={key}
									className="flex cursor-pointer items-center gap-2 rounded-lg border border-neutral-200 bg-white p-2"
								>
									<input
										type="checkbox"
										checked={
											formData.riesgos[key as keyof RiesgosData['riesgos']] ??
											false
										}
										onChange={() =>
											toggleRiesgo(key as keyof RiesgosData['riesgos'])
										}
										className="size-4 rounded border-neutral-300"
									/>
									<span className="text-xs text-neutral-700">{label}</span>
								</label>
							))}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Diseño Antisísmico (opcional)
							</label>
							<input
								type="text"
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								placeholder="¿Cumple normas antisísmicas? ¿Es superior?"
								value={formData.disenoAntisismico}
								onChange={(e) =>
									setFormData({
										...formData,
										disenoAntisismico: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Características del Subsuelo (opcional)
							</label>
							<input
								type="text"
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								placeholder="Roca, grava, arena, arcilla, rellenos, fallas geológicas..."
								value={formData.caracteristicasSubsuelo}
								onChange={(e) =>
									setFormData({
										...formData,
										caracteristicasSubsuelo: e.target.value,
									})
								}
							/>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="flex flex-col gap-2">
								<label className="text-sm font-medium text-neutral-700">
									Nivel Freático
								</label>
								<input
									type="text"
									className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
									placeholder="Profundidad..."
									value={formData.nivelFreatico}
									onChange={(e) =>
										setFormData({ ...formData, nivelFreatico: e.target.value })
									}
								/>
							</div>

							<div className="flex flex-col gap-2">
								<label className="text-sm font-medium text-neutral-700">
									Cuerpos de Agua
								</label>
								<input
									type="text"
									className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
									placeholder="Ríos, lagos cercanos..."
									value={formData.cuerposAguaCercanos}
									onChange={(e) =>
										setFormData({
											...formData,
											cuerposAguaCercanos: e.target.value,
										})
									}
								/>
							</div>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Condiciones Meteorológicas (opcional)
							</label>
							<input
								type="text"
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								placeholder="Temporadas de lluvia, precipitaciones, riesgo tempestad..."
								value={formData.condicionesMeteorologicas}
								onChange={(e) =>
									setFormData({
										...formData,
										condicionesMeteorologicas: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Edificaciones Linderas (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Propiedades vecinas que puedan ser afectadas..."
								value={formData.edificacionesLinderas}
								onChange={(e) =>
									setFormData({
										...formData,
										edificacionesLinderas: e.target.value,
									})
								}
							/>
						</div>

						<div className="rounded-xl bg-orange-50 p-3">
							<p className="font-inter text-xs font-medium text-orange-800">
								Coberturas Adicionales
							</p>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Coberturas Adicionales Requeridas
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={3}
								placeholder="Remoción escombros, huelga/motín, mantenimiento, horas extras, flete aéreo, bienes fuera del sitio, transportes..."
								value={formData.coberturasAdicionales}
								onChange={(e) =>
									setFormData({
										...formData,
										coberturasAdicionales: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Responsabilidad Civil (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="RC Extracontractual, daños materiales, lesiones, RC Cruzada..."
								value={formData.responsabilidadCivil}
								onChange={(e) =>
									setFormData({
										...formData,
										responsabilidadCivil: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Equipos y Maquinarias (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Lista de equipos con descripción, marca, modelo, valor..."
								value={formData.equiposMaquinarias}
								onChange={(e) =>
									setFormData({
										...formData,
										equiposMaquinarias: e.target.value,
									})
								}
							/>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="flex flex-col gap-2">
								<label className="text-sm font-medium text-neutral-700">
									Moneda
								</label>
								<select
									className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
									value={formData.moneda}
									onChange={(e) =>
										setFormData({ ...formData, moneda: e.target.value })
									}
								>
									<option value="Pesos">Pesos</option>
									<option value="Dólares">Dólares USD</option>
								</select>
							</div>

							<div className="flex flex-col gap-2">
								<label className="text-sm font-medium text-neutral-700">
									Valores a Riesgo
								</label>
								<input
									type="text"
									className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
									placeholder="Suma total asegurada"
									value={formData.valoresRiesgo}
									onChange={(e) =>
										setFormData({ ...formData, valoresRiesgo: e.target.value })
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar Cotización
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
